/* eslint-disable sonarjs/no-nested-functions */
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Dispatch, FC, useEffect, useMemo, useState } from "react";
import { Table } from "shared/app/Table";
import { BaseInput, Select } from "shared/components";
import { InputSize } from "shared/components/forms/DateInput";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import templatesApi from "api/landlord/templates";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { formatTitle } from "utils/formatTitle";
import { plural } from "pluralize";
import { getStateLabel } from "utils/stateCodes";
import { useNavigate } from "react-router-dom";
import { PropertyGroup as PropertyGroupCamelCase } from "views/property-groups-configuration/helpers/group_tree";
import { useQuery } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { PropertyGroupOptionBadgeList } from "shared/components/PropertyGroupOptionBadgeList";

interface ConfirmGroupsTableProps {
  groups: PropertyGroupCamelCase[];
  isLoading: boolean;
  selectedTemplates: {
    id: number | string;
    verification_template_id: string;
  }[];
  setSelectedTemplates: Dispatch<
    React.SetStateAction<
      {
        id: string;
        verification_template_id: string;
      }[]
    >
  >;
  selectedSplitOptions: PropertyGroupSplitBy[];
}

export const ConfirmGroupsTable: FC<ConfirmGroupsTableProps> = ({
  groups,
  isLoading,
  selectedTemplates,
  setSelectedTemplates,
  selectedSplitOptions
}) => {
  const { Properties } = useUnitsPropertiesDictionary();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["TEMPLATES"],
    queryFn: () =>
      templatesApi
        .getTemplates({ page: 1, per_page: 999 })
        .then((res) => res.items),
    placeholderData: keepPreviousData
  });

  const navigate = useNavigate();

  const defaultTemplate = useMemo(() => {
    if (!templates) return null;
    const defaultTemplate = templates.find(
      (template) => template.default === true
    );
    if (!defaultTemplate) return null;

    setSelectedTemplates(
      groups.map((group) => ({
        id: group.identity,
        verification_template_id:
          group.verificationTemplateIdentifier || defaultTemplate.id
      }))
    );
    return defaultTemplate;
  }, [templates]);

  const templateOptions = useMemo(
    () =>
      templates?.map((template) => ({
        value: template.id,
        label: template.name
      })) || [],
    [templates]
  );

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;

    const searchTermLower = searchTerm.toLowerCase();
    return groups.filter((group) => {
      if (
        group.titleChain[group.titleChain.length - 1]
          .toLowerCase()
          .includes(searchTermLower)
      )
        return true;

      if (
        group.titleChain.some((title) =>
          title.toLowerCase().includes(searchTermLower)
        )
      )
        return true;

      if (
        group.splitDataChain.some((split) =>
          split.splitValues.some((value) =>
            value.toLowerCase().includes(searchTermLower)
          )
        )
      )
        return true;

      return false;
    });
  }, [groups, searchTerm]);

  useEffect(() => {
    if (
      groups.length >= 1 &&
      groups[0].titleChain.length !== selectedSplitOptions.length + 1
    )
      navigate(
        `/property-groups/configuration?step=${selectedSplitOptions[0]}`
      );
  }, [groups, selectedSplitOptions]);

  const columns = useMemo<ColumnDef<PropertyGroupCamelCase>[]>(() => {
    const splitOptionColumns = selectedSplitOptions.map((option, index) => ({
      id: `${option}`,
      accessorKey: option,
      size: 160,
      header: formatTitle(propertyGroupTitleMap[option]),
      cell: ({ row }) => {
        const splitDataItem =
          row.original.split_data_chain?.[index + 1] ||
          row.original.splitDataChain?.[index + 1];

        if (!splitDataItem) return null;

        const isOther = splitDataItem.is_other || splitDataItem.isOther;
        const values =
          splitDataItem.split_values || splitDataItem.splitValues || [];

        const formatValue = (value: string) => {
          if (option === PropertyGroupSplitBy.STATE)
            return getStateLabel(value);
          if (option === PropertyGroupSplitBy.CITY) return formatTitle(value);
          return value;
        };

        const optionTitle = propertyGroupTitleMap[option] || option;

        return (
          <PropertyGroupOptionBadgeList
            values={values}
            formatValue={formatValue}
            isOther={isOther}
            otherTitle={plural(optionTitle)}
          />
        );
      }
    }));

    return [
      {
        id: "index fixed_width",
        accessorKey: "id",
        header: "#",
        size: 30,
        cell: ({ row }) => (
          <div className="text-content-1000 font-medium">
            {row.original.index}
          </div>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Group name",
        cell: ({ row }) => (
          <p className="text-content-1000 font-medium">
            {row.original.titleChain[row.original.titleChain.length - 1]}
          </p>
        )
      },
      ...splitOptionColumns,
      {
        id: "totalProperties fixed_width",
        accessorKey: "totalProperties",
        header: Properties,
        size: 50,
        cell: ({ row }) => (
          <div className="text-content-1000">
            {row.original.totalProperties || 0}
          </div>
        )
      },
      {
        id: "actions fixed_width",
        header: "Screening templates",
        size: 300,
        cell: ({ row }) => {
          const groupId = row.original.identity;
          const selectedTemplate = selectedTemplates.find(
            (template) => template.id === groupId
          );

          return (
            <Select
              size={InputSize.SM}
              id={`template-${groupId}`}
              searchable={true}
              hideOptional
              value={selectedTemplate?.verification_template_id}
              options={templateOptions}
              onChange={(e) => {
                const value = String(e.target.value);
                setSelectedTemplates((prev) =>
                  prev.map((template) =>
                    template.id === groupId
                      ? { ...template, verification_template_id: value }
                      : template
                  )
                );
              }}
            />
          );
        }
      }
    ];
  }, [
    defaultTemplate,
    selectedTemplates,
    templateOptions,
    selectedSplitOptions,
    Properties
  ]);

  const table = useReactTable({
    data: filteredGroups,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  if (isLoadingTemplates)
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="max-w-[271px] pt-2">
        <BaseInput
          id="search"
          autoComplete="off"
          placeholder="Search group"
          size={InputSize.SM}
          value={searchTerm}
          onChange={(value) => setSearchTerm(value)}
        />
      </div>

      <Table
        table={table}
        rowHeight="90px"
        tableWrapperClassName="min-h-[calc(100vh-350px)]"
        isLoading={isLoading}
      />
    </div>
  );
};
